import type { BidsSearchResult } from '@/queries/uniqueUserBids.types'

/**
 * BidPseudoStatus values align with design cases.
 */
export type BidPseudoStatus =
  | 'NO_BID'
  | 'LOSING'
  | 'LOST'
  | 'LOST_CANCEL'
  | 'WINNING'
  | 'WON'
  | 'WON_CANCEL'
  | 'WON_CREATED'
  | 'WON_ORDER_CANCEL'

/*
 * "Cancellation" in the UI means either:
 * - The auction was canceled
 * - An order was created, but it was closed without being completed (e.g
 *   buyer didn't pay). This is reflected in the closure reason.
 *
 * The `bidData.winning` field indicates if the bid is the leading bid on
 * an auction. This needs to be considered along with the cancellation
 * status, and the auction closed status, to determine which UI case we're
 * in. E.g. winning + auction closed means the bid "won". Not winning +
 * auction not closed means "losing".
 *
 * Note that bids and auctions can both be canceled and those cancellations
 * mean different things. An auction being canceled means the goods are no
 * longer for sale. A bid being canceled means the bid can no longer
 * potentially win an auctions. "Canceled" in the UI here refers to the
 * _auction's_ cancellation status, not the bid's.
 */
export const getBidPseudoStatus = (
  bidSearchResult: BidsSearchResult
): BidPseudoStatus => {
  const { auction, order, winning } = bidSearchResult
  if (
    !auction.canceled &&
    (order?.closure?.reason === 'NON_PAYING_SUBJECT' ||
      order?.closure?.reason === 'PARTY_REQUESTED')
  ) {
    return winning ? 'WON_ORDER_CANCEL' : 'LOST_CANCEL'
  } else if (auction.canceled) {
    return winning ? 'WON_CANCEL' : 'LOST_CANCEL'
  } else if (auction.closed) {
    return winning ? (order ? 'WON_CREATED' : 'WON') : 'LOST'
  } else {
    return winning ? 'WINNING' : 'LOSING'
  }
}

type BidPseudoState = {
  canceled: boolean
  disabled: boolean
  lost: boolean
  status: BidPseudoStatus
}

export const getBidPseudoState = (
  bidSearchResult: BidsSearchResult
): BidPseudoState => {
  const status = getBidPseudoStatus(bidSearchResult)

  const canceled =
    status === 'LOST_CANCEL' ||
    status == 'WON_CANCEL' ||
    status === 'WON_ORDER_CANCEL'

  const disabled = canceled || status === 'LOST'

  const lost = status === 'LOST' || status === 'LOST_CANCEL'

  return {
    canceled,
    disabled,
    lost,
    status,
  }
}
